import type { LocalFile, LocalOrSavedFile } from '@frontend/api-types';
import { FILE_COUNT_LIMIT } from '@frontend/constants';
import { identity, mergeAndDedupe } from '@frontend/duck-tape';
import { makeLocalFileList } from '@frontend/forms';
import { useState, useVisibilityController } from '@frontend/react';
import type { Shortcut } from '@frontend/web-react';
import {
  ActionButton,
  DragAndDropModal,
  FilePreviewModal,
  getHotkeyHandler,
  Icon,
  LinkButton,
  mimeTypesMap,
  showToast,
  XStack,
  YStack,
} from '@frontend/web-react';
import { applyClass, type ClassName, concatClasses, safeCss } from '@frontend/web-utils';
import { useTestIds } from '@frontend/web/hooks/useTestIds';
import { FileButton as MFileButton, Textarea, type TextareaProps } from '@mantine/core';
import { FilePreview } from './FilePreview';

type ChatFooterProps = {
  autoFocus?: boolean;
  className?: ClassName;
  fileCountLimit?: number;
  filesValue: LocalFile[];
  isCompleteLoading?: boolean;
  isSnoozeLoading?: boolean;
  isStreamResponseLoading?: boolean;
  isSubmitLoading?: boolean;
  isUnsnoozeLoading?: boolean;
  muted?: boolean;
  onChangeFiles?: (files: LocalFile[]) => void;
  onChangeText: (text: string) => void;
  onClickInterrupt?: () => Promise<void> | void;
  onClickSubmit: () => Promise<void> | void;
  onComplete?: () => Promise<void> | void;
  onSnooze?: () => Promise<void> | void;
  onUnsnooze?: () => Promise<void> | void;
  placeholder?: string;
  submitDisabled?: boolean;
  submitShortcut?: Shortcut;
  textInputDisabled?: boolean;
  textInputRef?: React.RefObject<HTMLTextAreaElement>;
  textValue: string;
};

export const ChatFooter = ({
  autoFocus,
  className,
  fileCountLimit = FILE_COUNT_LIMIT,
  filesValue,
  isCompleteLoading,
  isSnoozeLoading,
  isStreamResponseLoading,
  isSubmitLoading,
  isUnsnoozeLoading,
  muted,
  onChangeFiles,
  onChangeText,
  onClickInterrupt,
  onClickSubmit,
  onComplete,
  onSnooze,
  onUnsnooze,
  placeholder = 'Enter a message...',
  submitDisabled,
  submitShortcut,
  textInputDisabled,
  textInputRef,
  textValue,
}: ChatFooterProps) => {
  const [isPreviewFileModalOpen, { close: closeFilePreviewModal, open: openFilePreviewModal }] =
    useVisibilityController();

  const [filePreviewModalSelectedFile, setFilePreviewModalSelectedFile] = useState<LocalOrSavedFile>();

  const testIds = useTestIds(['/app/taskId.sendMessageButton']);

  const onRemoveFile = (file: LocalFile) => {
    onChangeFiles?.(filesValue?.filter(({ name }) => name !== file.name));
  };

  const onChangeComposed = (newFiles: LocalFile[]) => {
    const dedupedFiles = mergeAndDedupe(filesValue, newFiles, ({ name }) => name);
    if (dedupedFiles.length > fileCountLimit) {
      onChangeFiles?.(dedupedFiles.slice(0, fileCountLimit));
      showToast({
        message: `You can only attach up to ${fileCountLimit} files. The extra files were not included.`,
        title: 'File limit reached',
        variant: 'warning',
      });
    } else {
      onChangeFiles?.(dedupedFiles);
    }
  };

  const onClickFilePreview = (file: LocalOrSavedFile) => {
    setFilePreviewModalSelectedFile(file);
    openFilePreviewModal();
  };

  const textareaInputClassNames: TextareaProps['classNames'] = {
    input: safeCss(
      'py-md px-none text-p-md outline-none text-textPrimary placeholder:text-textTertiary border-none bg-trueWhite',
    ),
    root: safeCss('gap-y-xs flex flex-col flex-1 w-full'),
  };

  const fileLimitReached = fileCountLimit <= filesValue.length;

  return (
    <YStack
      className={concatClasses(
        'border-borderPrimary border-1 shadow-one px-md pt-sm pb-sm rounded-lg gap-sm bg-trueWhite',
        applyClass(muted, 'opacity-60'),
        className,
      )}
    >
      <DragAndDropModal fileLimitReached={fileLimitReached} onFilesDropped={onChangeComposed} />
      {filesValue.length ? (
        <XStack className="gap-sm flex-wrap pt-sm">
          {filesValue?.map((file) => (
            <FilePreview
              file={file}
              key={file.name}
              onClick={() => onClickFilePreview(file)}
              onRemove={() => onRemoveFile(file)}
            />
          ))}
        </XStack>
      ) : null}
      <Textarea
        autoFocus={autoFocus}
        autosize
        classNames={textareaInputClassNames}
        disabled={textInputDisabled}
        maxRows={10}
        minRows={1}
        name="message"
        onChange={(event) => onChangeText(event.currentTarget.value)}
        onKeyDown={
          submitShortcut ? getHotkeyHandler([[submitShortcut, submitDisabled ? identity : onClickSubmit]]) : undefined
        }
        placeholder={placeholder}
        ref={textInputRef}
        value={textValue}
      />
      <XStack className="flex gap-sm" onClick={() => textInputRef?.current?.focus()}>
        {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
        <XStack className="grow-1 gap-[12px]">
          {onChangeFiles ? (
            // We could add more file types here if we want to support more file types
            <MFileButton
              accept={Object.keys(mimeTypesMap).join(',')}
              multiple
              onChange={(files) => onChangeComposed(makeLocalFileList(files))}
            >
              {(buttonProps) => <LinkButton label="Attach" leftIconName="IconPaperclip" type="h6" {...buttonProps} />}
            </MFileButton>
          ) : null}
          {onSnooze ? (
            <ActionButton
              isLoading={isSnoozeLoading}
              label="Snooze"
              leftIconName="IconAlarmSnooze"
              onClick={onSnooze}
            />
          ) : null}
          {onUnsnooze ? (
            <ActionButton
              isLoading={isUnsnoozeLoading}
              label="Unsnooze"
              leftIconName="IconAlarmOff"
              onClick={onUnsnooze}
            />
          ) : null}
          {onComplete ? (
            <ActionButton
              isLoading={isCompleteLoading}
              label="Complete"
              leftIconName="IconCheck"
              onClick={onComplete}
              variant="filled"
            />
          ) : null}
        </XStack>
        <XStack alignItems="center">
          <Icon
            color={isStreamResponseLoading && onClickInterrupt ? 'secondary' : 'primary'}
            disabled={submitDisabled}
            enableBackgroundColor
            isLoading={isSubmitLoading}
            name={isStreamResponseLoading && onClickInterrupt ? 'IconPlayerPauseFilled' : 'IconArrowUp'}
            onClick={isStreamResponseLoading && onClickInterrupt ? onClickInterrupt : onClickSubmit}
            rounded
            testId={testIds['/app/taskId.sendMessageButton']}
          />
        </XStack>
      </XStack>
      <FilePreviewModal
        file={filePreviewModalSelectedFile}
        isOpen={isPreviewFileModalOpen}
        onClose={closeFilePreviewModal}
        title={
          filePreviewModalSelectedFile?.__typename === 'LocalFile'
            ? filePreviewModalSelectedFile.name
            : filePreviewModalSelectedFile?.key
        }
      />
    </YStack>
  );
};
